import styled from '@emotion/styled';

import { Grid, Card, CardHeader, CardContent, CardActions, Button, Typography, Divider, LinearProgress } from '@mui/material';

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';

import { BasicFlex, Notes, LabelUpper } from '../../../utils/Common';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import { fetchPromotionStatus } from '../apiService';

import { useQuery } from '@tanstack/react-query';

// use pageData context
import { usePageData } from '../../../contexts/PageData';

import { formatGenericDateTime, formatCurrency } from '../../../utils/util';

import { startCase } from 'lodash';

import Lottie from '../../../utils/Lotties';
import animationData from '../../../assets/animations/email_paper_plane.json';

const ProgressLabels = styled(BasicFlex)`
  margin-bottom: 1rem;
`

const ProgressPct = styled(BasicFlex)`
  justify-content: flex-end;
  color: ${props => props.theme.colors.lightText};
  text-align: right;
  margin-top: .5rem;
`

const StatusDivision = styled(BasicFlex)`
 border-right: 1px solid ${props => props.theme.colors.border};
 flex-direction: column;
 flex-grow: 1;
 justify-content: center;
 /* align-items: flex-start; */
 /* padding: 1rem 0; */
 gap: .5rem;


 &:last-child {
   border-right: none;
 }
`

const MessageBody = styled.div`
  margin-top: .5rem;
  font-style: italic;
`

const StatusLabel = styled.div`
  text-transform: uppercase;
  font-size: 1.2rem;
  font-weight: 500;
  color: ${props => props.theme.colors.mildText};
  letter-spacing: .5px;
`

const StatNumber = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${props => props?.$color === "success" ? '#488639' : props?.$color === "error" ? '#d32f2f' : ""};
`

const StatInfo = styled.div`
  font-family: monospace;
`

const GeneralTab = () => {
  const { data = {} } = usePageData();

  let promotionId = data.sms_promotion_id

  const basicDetails = [
    {header: "Promotion ID", info: data.sms_promotion_id},
    {header: "Description", info: startCase(data.details)},
    {header: "Start Time", info: data.started_at, dataType: "dateTime"},
    
  ]

  const statusInfo = [
  {header: "Status", info: <MultiStatusChip value={data.status} values={[{ label: "In Progress", value: "sending", status: "success" }, { label: "Completed", value: "complete", status: "success" }, { label: "Failed", value: "failed", status: "error" }]} />},
  {header: "Number of Customers", info: data.total_count, dataType: "number"},
  {header: "Successful", info: data.sent_count, dataType: "number"},
  {header: "Failed", info: data.failed_count, dataType: "number"},
    {header: "Time Completed", info: data.completed_at, dataType: "dateTime"},
  ]

  const { data: promotionStatus } = useQuery({
    queryKey: ['sms-promotion', promotionId],
    queryFn: () => fetchPromotionStatus(promotionId),
    enabled: !!promotionId,
    refetchInterval: (query) => {
      const status = query.state.data?.promotion?.status;
      if (!status || ['complete', 'failed'].includes(status)) return false;
      return 3000;
    },
  });

  const processed = promotionStatus?.sent_count + promotionStatus?.failed_count || 0;
  const progress = promotionStatus?.total_count > 0
  ? Math.round((processed / promotionStatus?.total_count) * 100)
  : 0;

  const displayCost = promotionStatus?.status === 'complete' && promotionStatus?.ending_sms_balance != null
  ? (Math.abs(promotionStatus?.ending_sms_balance - promotionStatus?.beginning_sms_balance) || 0)
  : (promotionStatus?.sent_count * 0.01) || 0;

  return (
    <Grid container spacing={3}>

      <Grid size={{xs: 12, md: 6}}>
        <Card variant='outlined'>
          <CardHeader title="Basic Details" />
          <CardContent>
            <CardBlockContainer>
              {basicDetails.map((detail, index) => <CardBlock key={index} {...detail} />)}

              <CardSpacer />

              <CardBlockHeader>Activity</CardBlockHeader>
              {statusInfo.map((detail, index) => <CardBlock key={index} {...detail} />)}
            {/* <CardSpacer /> */}
              
            </CardBlockContainer>
            {/* <LabelUpper $normal $size="1.2rem">Message Body</LabelUpper>
            <MessageBody>
              {data.message}
            </MessageBody> */}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{xs: 12, md: 6}}>
        <Card variant='outlined' sx={{pb: "0 !important"}}>
          <CardHeader title="Status" subheader={startCase(data.details)} action={<MultiStatusChip value={data.status} values={[{ label: "In Progress", value: "sending", status: "success" }, { label: "Completed", value: "complete", status: "success" }, { label: "Failed", value: "failed", status: "error" }]} />} slotProps={{title: {sx: {
            fontSize: "1.2rem", textTransform: "uppercase"
          }},
          subheader: {sx: {
            fontSize: "1.4rem", fontWeight: "500"
          }},
          action: {sx: {
            position: "relative",
            top: "1rem", right: "1rem"
          }}
          }} />
          <Divider />
          {promotionStatus?.status === 'sending' ? <><CardContent sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
            <Lottie lottieFile={animationData} containerXY={{containerHeight: "8rem", containerWidth: "8rem"}} />
            </CardContent>
          <Divider />
          </> : null}
          <CardContent>
            <ProgressLabels>
              <div>Delivery Progress</div>
              <div>{processed} / {data?.total_count}</div>
              
            </ProgressLabels>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={
                promotionStatus?.status === 'complete' ? 'success' :
                promotionStatus?.status === 'failed'   ? 'error'   :
                'primary'
              }
            />
            <ProgressPct>
              <span>{progress}%</span>
            </ProgressPct>
          </CardContent>
          <Divider />
          <CardContent sx={{display: "flex", justifyContent: "space-between", py: "0"}}>
            <StatusDivision py="1rem">
              <StatNumber>{data.total_count}</StatNumber>
             <StatusLabel>Total</StatusLabel>
            </StatusDivision>
            <StatusDivision py="1rem">
              <StatNumber $color="success">{data.sent_count}</StatNumber>
              <StatusLabel>Sent</StatusLabel>
            </StatusDivision>
            <StatusDivision py="1rem">
              <StatNumber $color="error">{data.failed_count}</StatNumber>
              <StatusLabel>Failed</StatusLabel>
            </StatusDivision>
          </CardContent>
          <Divider />
          <CardContent sx={{backgroundColor: "grey.200"}}>
            <LabelUpper $size="1.2rem">Message Body</LabelUpper>
            <MessageBody>
            "{data.message}"
            </MessageBody>

          </CardContent>
          <Divider />
          <CardContent sx={{display: "flex", justifyContent: "space-between", py: "0", pb: "0 !important"}}>
            <StatusDivision align="flex-start" py="1rem">
              <StatusLabel>Started At</StatusLabel>
              <StatInfo>{formatGenericDateTime(data.started_at)}</StatInfo>
            </StatusDivision>
            <StatusDivision align="flex-start" px="1rem" py="1rem">
              <StatusLabel>Est. Cost</StatusLabel>
              <StatInfo>{formatCurrency(displayCost)}</StatInfo>
            </StatusDivision>
          </CardContent>
        </Card>
      </Grid>
      

      {/* <Grid size={{xs: 12, md: 8}}>
        <Card variant='outlined'>
          <CardHeader title={<BasicFlex gap="1rem">Specialist History <HistoryRoundedIcon /></BasicFlex>} />
          <CardContent>
            Timeline goes here
          </CardContent>
        </Card>
      </Grid> */}
 
    </Grid>
  );

};

export default GeneralTab;